import type {
  Cell,
  LifeEngine,
  LifeRegion,
  LifeSnapshot,
} from "./engine";

function assertDimension(value: number, name: string): number {
  if (!Number.isSafeInteger(value) || value <= 0) {
    throw new RangeError(`${name} must be a positive integer`);
  }
  return value;
}

export class NaiveLifeEngine implements LifeEngine {
  private current: Uint8Array;
  private next: Uint8Array;
  private gridWidth: number;
  private gridHeight: number;

  constructor(width: number, height: number) {
    this.gridWidth = assertDimension(width, "width");
    this.gridHeight = assertDimension(height, "height");
    this.current = new Uint8Array(this.width * this.height);
    this.next = new Uint8Array(this.current.length);
  }

  get width(): number {
    return this.gridWidth;
  }

  get height(): number {
    return this.gridHeight;
  }

  step(): void {
    for (let y = 0; y < this.height; y += 1) {
      for (let x = 0; x < this.width; x += 1) {
        const index = this.indexOf(x, y);
        const wasAlive = this.current[index] === 1;
        const neighbors = this.countNeighbors(x, y);
        const isAlive =
          neighbors === 3 || (wasAlive && neighbors === 2);

        this.next[index] = isAlive ? 1 : 0;
      }
    }

    [this.current, this.next] = [this.next, this.current];
  }

  clear(): void {
    this.current.fill(0);
    this.next.fill(0);
  }

  resize(width: number, height: number): void {
    const nextWidth = assertDimension(width, "width");
    const nextHeight = assertDimension(height, "height");
    if (nextWidth === this.width && nextHeight === this.height) {
      return;
    }

    const resized = new Uint8Array(nextWidth * nextHeight);
    const xOffset = Math.floor((nextWidth - this.width) / 2);
    const yOffset = Math.floor((nextHeight - this.height) / 2);
    for (let y = 0; y < this.height; y += 1) {
      for (let x = 0; x < this.width; x += 1) {
        if (this.current[this.indexOf(x, y)] !== 1) {
          continue;
        }
        const nextX = x + xOffset;
        const nextY = y + yOffset;
        if (this.isInside(nextX, nextY, nextWidth, nextHeight)) {
          resized[nextY * nextWidth + nextX] = 1;
        }
      }
    }

    this.gridWidth = nextWidth;
    this.gridHeight = nextHeight;
    this.current = resized;
    this.next = new Uint8Array(resized.length);
  }

  replace(cells: Iterable<Cell>): void {
    this.clear();
    for (const cell of cells) {
      this.setCell(cell.x, cell.y, true);
    }
  }

  getCell(x: number, y: number): boolean {
    return this.isInside(x, y) && this.current[this.indexOf(x, y)] === 1;
  }

  setCell(x: number, y: number, alive: boolean): void {
    if (!this.isInside(x, y)) {
      return;
    }

    this.current[this.indexOf(x, y)] = alive ? 1 : 0;
  }

  snapshot(region?: LifeRegion): LifeSnapshot {
    if (!region) {
      return {
        x: 0,
        y: 0,
        width: this.width,
        height: this.height,
        cells: this.current.slice(),
      };
    }
    const { x, y, width, height } = region;
    if (
      !Number.isSafeInteger(x) ||
      !Number.isSafeInteger(y) ||
      !Number.isSafeInteger(width) ||
      width <= 0 ||
      !Number.isSafeInteger(height) ||
      height <= 0
    ) {
      throw new RangeError(
        "snapshot region must use integer coordinates and positive dimensions",
      );
    }

    const cells = new Uint8Array(width * height);
    for (let regionY = 0; regionY < height; regionY += 1) {
      const worldY = y + regionY;
      if (worldY < 0 || worldY >= this.height) {
        continue;
      }
      for (let regionX = 0; regionX < width; regionX += 1) {
        const worldX = x + regionX;
        if (this.getCell(worldX, worldY)) {
          cells[regionY * width + regionX] = 1;
        }
      }
    }

    return {
      x,
      y,
      width,
      height,
      cells,
    };
  }

  private countNeighbors(x: number, y: number): number {
    let count = 0;
    const minX = Math.max(0, x - 1);
    const maxX = Math.min(this.width - 1, x + 1);
    const minY = Math.max(0, y - 1);
    const maxY = Math.min(this.height - 1, y + 1);

    for (let neighborY = minY; neighborY <= maxY; neighborY += 1) {
      for (let neighborX = minX; neighborX <= maxX; neighborX += 1) {
        if (
          (neighborX !== x || neighborY !== y) &&
          this.current[this.indexOf(neighborX, neighborY)] === 1
        ) {
          count += 1;
        }
      }
    }
    return count;
  }

  private isInside(
    x: number,
    y: number,
    width = this.width,
    height = this.height,
  ): boolean {
    return (
      Number.isInteger(x) &&
      Number.isInteger(y) &&
      x >= 0 &&
      x < width &&
      y >= 0 &&
      y < height
    );
  }

  private indexOf(x: number, y: number): number {
    return y * this.width + x;
  }
}
